import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-jalaali';
import PersianDatePicker from '../components/PersianDatePicker';
import { HIS_MENU_TITLE } from '../config/hisSections';
import { canAccessHisReport } from '../utils/hisReportAccess';
import { getApiBaseUrl } from '../utils/getApiUrl';
import { rialMoneyStat } from '../utils/formatMoneyFa';

type PaymentType = 'cash' | 'insurance' | 'all';
type DatePreset = 'today' | 'month' | 'last7' | 'custom';
type DetailKind = 'booking' | 'admission' | 'dowork';

interface HisMachine {
  machine_id: number;
  machine_name: string;
  display_name: string;
}

interface ConversionItem {
  appointment_id: number;
  tracking_code: string;
  appointment_date: string;
  appointment_time: string;
  patient_name: string;
  national_id: string;
  phone: string;
  matched: boolean;
  match_method?: string | null;
  recept_date?: string | null;
  his_record_code?: string | null;
  his_patient_name?: string | null;
  has_dowork: boolean;
  cash_amount: number;
  insurance_amount: number;
  total_amount: number;
}

interface ConversionReport {
  from_date: string;
  to_date: string;
  match_window_days: number;
  payment_type: PaymentType;
  payment_type_label: string;
  machine_ref: number | null;
  machine_label: string | null;
  booking_count: number;
  admission_count: number;
  dowork_count: number;
  total_cash: number;
  total_insurance: number;
  total_amount: number;
  conversion_rate: number;
  items: ConversionItem[];
  warnings: string[];
}

interface DetailResponse {
  kind: DetailKind;
  kind_label: string;
  count: number;
  items: ConversionItem[];
}

interface ConnectionStatus {
  configured: boolean;
  ok?: boolean;
}

const defaultFromDate = (): string => moment().startOf('jMonth').format('jYYYY/jMM/jDD');
const defaultToDate = (): string => moment().format('jYYYY/jMM/jDD');

const PAYMENT_OPTIONS: { id: PaymentType; label: string; desc: string }[] = [
  { id: 'all', label: 'همه', desc: 'نقدی + بیمه' },
  { id: 'cash', label: 'نقدی', desc: 'فقط سهم بیمار' },
  { id: 'insurance', label: 'بیمه', desc: 'فقط سهم بیمه' },
];

const DATE_PRESETS: { id: DatePreset; label: string; apply: () => { from: string; to: string } }[] = [
  {
    id: 'today',
    label: 'امروز',
    apply: () => {
      const d = moment().format('jYYYY/jMM/jDD');
      return { from: d, to: d };
    },
  },
  {
    id: 'month',
    label: 'ماه جاری',
    apply: () => ({
      from: moment().startOf('jMonth').format('jYYYY/jMM/jDD'),
      to: moment().format('jYYYY/jMM/jDD'),
    }),
  },
  {
    id: 'last7',
    label: '۷ روز اخیر',
    apply: () => ({
      from: moment().subtract(6, 'day').format('jYYYY/jMM/jDD'),
      to: moment().format('jYYYY/jMM/jDD'),
    }),
  },
];

const CARD_TIPS = {
  reception:
    'نوبت‌های آنلاین تاییدشده‌ای که در HIS پذیرش شده‌اند (به ثمر رسیده‌اند).',
  booking: 'تعداد نوبت‌های آنلاین با وضعیت تاییدشده که تاریخ نوبتشان در بازه انتخابی است.',
  dowork: 'از نوبت‌های به ثمر رسیده، آن‌هایی که انجام‌کار هم در HIS دارند.',
  cashAmount: 'جمع مبلغ نقدی پذیرش‌های منتج از نوبت آنلاین.',
  insuranceAmount: 'جمع مبلغ بیمه پذیرش‌های منتج از نوبت آنلاین.',
  totalAmount: 'جمع نقدی + بیمه برای پذیرش‌های منتج از نوبت آنلاین.',
};

const StatCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  tip?: string;
  accent: 'indigo' | 'emerald' | 'sky' | 'violet' | 'amber' | 'rose' | 'teal';
  icon: React.ReactNode;
  onClick?: () => void;
}> = ({ label, value, sub, tip, accent, icon, onClick }) => {
  const accents = {
    indigo: 'from-indigo-500/10 to-indigo-600/5 text-indigo-600 border-indigo-100',
    emerald: 'from-emerald-500/10 to-emerald-600/5 text-emerald-600 border-emerald-100',
    sky: 'from-sky-500/10 to-sky-600/5 text-sky-600 border-sky-100',
    violet: 'from-violet-500/10 to-violet-600/5 text-violet-600 border-violet-100',
    amber: 'from-amber-500/10 to-amber-600/5 text-amber-600 border-amber-100',
    rose: 'from-rose-500/10 to-rose-600/5 text-rose-600 border-rose-100',
    teal: 'from-teal-500/10 to-teal-600/5 text-teal-600 border-teal-100',
  };
  const clickable = Boolean(onClick);

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={`group/card relative rounded-2xl border bg-gradient-to-br p-4 shadow-sm transition duration-200 ease-out hover:z-20 hover:-translate-y-1 hover:scale-[1.03] hover:shadow-xl ${accents[accent]} ${
        clickable ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400/40' : ''
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <p className="text-xs font-medium text-slate-500">{label}</p>
          {tip ? (
            <span className="group/tip relative inline-flex shrink-0">
              <button
                type="button"
                className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-200/80 text-[10px] font-bold text-slate-600 hover:bg-slate-300"
                aria-label={tip}
                onClick={(e) => e.stopPropagation()}
              >
                ؟
              </button>
              <span
                role="tooltip"
                className="pointer-events-none absolute right-0 top-full z-50 mt-2 hidden w-52 rounded-lg bg-slate-800 px-3 py-2 text-[11px] font-normal leading-relaxed text-white shadow-lg group-hover/tip:block"
              >
                {tip}
              </span>
            </span>
          ) : null}
        </div>
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/90 shadow-sm">
          {icon}
        </div>
      </div>
      <p dir="ltr" className="mt-3 text-right text-base font-bold tabular-nums tracking-tight text-slate-900 sm:text-lg">
        <span className="whitespace-nowrap">{value}</span>
        {sub ? (
          <span className="mr-1.5 whitespace-nowrap text-xs font-semibold text-slate-500">{sub}</span>
        ) : null}
      </p>
      {clickable ? (
        <p className="mt-2 text-[10px] font-medium text-slate-400 opacity-0 transition-opacity group-hover/card:opacity-100">
          برای مشاهده لیست کلیک کنید
        </p>
      ) : null}
    </div>
  );
};

const ICONS = {
  clipboard: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  ),
  currency: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  calendar: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  check: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

const matchMethodLabel = (method?: string | null) => {
  if (method === 'national_id') return 'کد ملی';
  if (method === 'phone') return 'موبایل';
  return '—';
};

const DetailModal: React.FC<{
  open: boolean;
  loading: boolean;
  error: string | null;
  data: DetailResponse | null;
  onClose: () => void;
}> = ({ open, loading, error, data, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/35 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ direction: 'rtl' }}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <div>
            <h3 className="text-sm font-bold text-slate-900">{data?.kind_label || 'جزئیات'}</h3>
            <p className="text-xs text-slate-500">
              {data ? `${data.count.toLocaleString('fa-IR')} مورد` : 'در حال بارگذاری...'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-100"
          >
            بستن
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <p className="py-10 text-center text-sm text-slate-500">در حال دریافت لیست...</p>
          ) : error ? (
            <p className="py-10 text-center text-sm text-rose-600">{error}</p>
          ) : !data?.items?.length ? (
            <p className="py-10 text-center text-sm text-slate-500">موردی یافت نشد</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-right text-xs">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">بیمار</th>
                    <th className="px-3 py-2 font-medium">کد ملی</th>
                    <th className="px-3 py-2 font-medium">موبایل</th>
                    <th className="px-3 py-2 font-medium">تاریخ نوبت</th>
                    <th className="px-3 py-2 font-medium">پذیرش</th>
                    <th className="px-3 py-2 font-medium">تطبیق</th>
                    <th className="px-3 py-2 font-medium">انجام کار</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.items.map((item) => (
                    <tr key={item.appointment_id} className="hover:bg-slate-50/80">
                      <td className="px-3 py-2 font-medium text-slate-800">{item.patient_name || '—'}</td>
                      <td className="px-3 py-2 font-mono" dir="ltr">
                        {item.national_id || '—'}
                      </td>
                      <td className="px-3 py-2 font-mono" dir="ltr">
                        {item.phone || '—'}
                      </td>
                      <td className="px-3 py-2" dir="ltr">
                        {item.appointment_date}
                        {item.appointment_time ? ` ${item.appointment_time}` : ''}
                      </td>
                      <td className="px-3 py-2" dir="ltr">
                        {item.recept_date || '—'}
                      </td>
                      <td className="px-3 py-2">{matchMethodLabel(item.match_method)}</td>
                      <td className="px-3 py-2">{item.has_dowork ? 'بله' : 'خیر'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const AdminHisOnlineConversion: React.FC = () => {
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(defaultToDate);
  const [datePreset, setDatePreset] = useState<DatePreset>('month');
  const [machines, setMachines] = useState<HisMachine[]>([]);
  const [selectedMachineRef, setSelectedMachineRef] = useState<string>('all');
  const [paymentType, setPaymentType] = useState<PaymentType>('all');
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connection, setConnection] = useState<ConnectionStatus | null>(null);
  const [report, setReport] = useState<ConversionReport | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailData, setDetailData] = useState<DetailResponse | null>(null);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    if (!canAccessHisReport()) {
      navigate('/admin/dashboard');
      return;
    }

    const loadStatus = async () => {
      try {
        const apiBaseUrl = getApiBaseUrl();
        const [statusRes, machinesRes] = await Promise.all([
          fetch(`${apiBaseUrl}/admin/reports/his/connection-status`, { headers: authHeaders() }),
          fetch(`${apiBaseUrl}/admin/reports/his/machines`, { headers: authHeaders() }),
        ]);
        if (statusRes.ok) setConnection(await statusRes.json());
        if (machinesRes.ok) {
          const data = await machinesRes.json();
          setMachines(data.items || []);
        }
      } catch {
        /* ignore */
      }
    };
    void loadStatus();
  }, [navigate, authHeaders]);

  const applyDatePreset = (preset: DatePreset) => {
    if (preset === 'custom') {
      setDatePreset('custom');
      return;
    }
    const p = DATE_PRESETS.find((x) => x.id === preset);
    if (!p) return;
    const { from, to } = p.apply();
    setFromDate(from);
    setToDate(to);
    setDatePreset(preset);
    setReport(null);
  };

  const fetchReport = async () => {
    if (!fromDate || !toDate) {
      setError('بازه زمانی را انتخاب کنید');
      return;
    }
    setLoadingReport(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        from_date: fromDate,
        to_date: toDate,
        payment_type: paymentType,
      });
      if (selectedMachineRef !== 'all') params.set('machine_ref', selectedMachineRef);

      const apiBaseUrl = getApiBaseUrl();
      const res = await fetch(
        `${apiBaseUrl}/admin/reports/his/online-conversion?${params}`,
        { headers: authHeaders() }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'خطا در دریافت گزارش');
      }
      setReport(await res.json());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'خطای ناشناخته');
    } finally {
      setLoadingReport(false);
    }
  };

  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setDetailLoading(false);
    setDetailError(null);
  }, []);

  const openDetail = useCallback(
    async (kind: DetailKind) => {
      if (!report) return;
      setDetailOpen(true);
      setDetailLoading(true);
      setDetailError(null);
      setDetailData(null);
      try {
        const params = new URLSearchParams({
          kind,
          from_date: report.from_date,
          to_date: report.to_date,
          payment_type: report.payment_type,
        });
        if (report.machine_ref != null) params.set('machine_ref', String(report.machine_ref));
        const apiBaseUrl = getApiBaseUrl();
        const res = await fetch(
          `${apiBaseUrl}/admin/reports/his/online-conversion/details?${params}`,
          { headers: authHeaders() }
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || 'خطا در دریافت جزئیات');
        }
        setDetailData(await res.json());
      } catch (err: unknown) {
        setDetailError(err instanceof Error ? err.message : 'خطای ناشناخته');
      } finally {
        setDetailLoading(false);
      }
    },
    [report, authHeaders]
  );

  const connectionBadge = connection ? (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
        connection.ok
          ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/80'
          : connection.configured
            ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200/80'
            : 'bg-rose-50 text-rose-700 ring-1 ring-rose-200/80'
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          connection.ok ? 'bg-emerald-500' : connection.configured ? 'bg-amber-500' : 'bg-rose-500'
        }`}
      />
      {connection.configured ? (connection.ok ? 'متصل' : 'مشکل اتصال') : 'اتصال برقرار نیست'}
    </span>
  ) : null;

  const displayTotal = report
    ? report.payment_type === 'cash'
      ? report.total_cash
      : report.payment_type === 'insurance'
        ? report.total_insurance
        : report.total_amount
    : 0;

  return (
    <div className="w-full space-y-4">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-l from-indigo-600 via-indigo-700 to-slate-800 px-5 py-4 text-white shadow-lg shadow-indigo-900/20 sm:px-6">
        <div
          className="pointer-events-none absolute -left-16 -top-16 h-48 w-48 rounded-full bg-white/10 blur-2xl"
          aria-hidden
        />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate('/admin/his')}
              className="mb-1 text-[11px] text-indigo-200 hover:text-white"
            >
              ← بازگشت به {HIS_MENU_TITLE}
            </button>
            <p className="text-xs font-medium uppercase tracking-wider text-indigo-200">نوبت آنلاین</p>
            <h1 className="mt-0.5 text-xl font-bold tracking-tight sm:text-2xl">به ثمر رسیدن نوبت آنلاین</h1>
            <p className="mt-1 hidden max-w-lg text-xs text-indigo-100/90 sm:block">
              نوبت‌های تاییدشده که در HIS پذیرش شده‌اند — تطبیق با کد ملی سپس موبایل
            </p>
          </div>
          {connectionBadge}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200/80 bg-white p-2 shadow-sm sm:p-2.5">
        <div className="flex flex-col gap-2 xl:flex-row xl:items-center">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <div className="flex gap-0.5 rounded-lg bg-slate-100 p-0.5">
              {DATE_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyDatePreset(p.id)}
                  className={`rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                    datePreset === p.id
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="flex items-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50/60">
              <div className="w-[7.25rem] border-l border-slate-200">
                <PersianDatePicker
                  compact
                  borderless
                  value={fromDate}
                  placeholder="از"
                  onChange={(v) => {
                    setFromDate(v);
                    setDatePreset('custom');
                    setReport(null);
                  }}
                />
              </div>
              <div className="w-[7.25rem]">
                <PersianDatePicker
                  compact
                  borderless
                  value={toDate}
                  placeholder="تا"
                  onChange={(v) => {
                    setToDate(v);
                    setDatePreset('custom');
                    setReport(null);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <div className="flex gap-0.5 rounded-lg bg-slate-100 p-0.5">
              {PAYMENT_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  title={opt.desc}
                  onClick={() => {
                    setPaymentType(opt.id);
                    setReport(null);
                  }}
                  className={`rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                    paymentType === opt.id
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <select
              value={selectedMachineRef}
              onChange={(e) => {
                setSelectedMachineRef(e.target.value);
                setReport(null);
              }}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-700"
            >
              <option value="all">همه خدمات</option>
              {machines.map((m) => (
                <option key={m.machine_id} value={String(m.machine_id)}>
                  {m.display_name || m.machine_name}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={() => void fetchReport()}
              disabled={loadingReport}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-60"
            >
              {loadingReport ? 'در حال اجرا...' : 'اجرای گزارش'}
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      {report?.warnings?.length ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
          {report.warnings.join(' | ')}
        </div>
      ) : null}

      {report ? (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span>
                بازه نوبت: {report.from_date} تا {report.to_date}
              </span>
              {report.machine_label ? (
                <>
                  <span>·</span>
                  <span>{report.machine_label}</span>
                </>
              ) : null}
            </div>
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-right">
              <p className="text-[10px] font-medium text-indigo-700">
                نرخ تبدیل کل نوبت آنلاین → پذیرش
              </p>
              <p className="text-sm font-bold tabular-nums text-indigo-900">
                {report.conversion_rate.toLocaleString('fa-IR')}٪
              </p>
              <p className="text-[10px] text-indigo-600/90">
                {report.admission_count.toLocaleString('fa-IR')} از{' '}
                {report.booking_count.toLocaleString('fa-IR')} نوبت تاییدشده در بازه
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <StatCard
              label="تعداد پذیرش"
              value={report.admission_count.toLocaleString('fa-IR')}
              tip={CARD_TIPS.reception}
              accent="sky"
              icon={ICONS.clipboard}
              onClick={() => void openDetail('admission')}
            />
            <StatCard
              label="نوبت‌دهی"
              value={report.booking_count.toLocaleString('fa-IR')}
              tip={CARD_TIPS.booking}
              accent="amber"
              icon={ICONS.calendar}
              onClick={() => void openDetail('booking')}
            />
            <StatCard
              label="انجام کار"
              value={report.dowork_count.toLocaleString('fa-IR')}
              tip={CARD_TIPS.dowork}
              accent="teal"
              icon={ICONS.check}
              onClick={() => void openDetail('dowork')}
            />
          </div>

          <div
            className={`grid gap-3 ${
              report.payment_type === 'all'
                ? 'grid-cols-1 sm:grid-cols-3'
                : 'grid-cols-1 sm:grid-cols-2'
            }`}
          >
            {report.payment_type !== 'insurance' && (
              <StatCard
                label={report.payment_type === 'cash' ? `جمع ${report.payment_type_label}` : 'نقدی'}
                {...rialMoneyStat(report.total_cash)}
                tip={CARD_TIPS.cashAmount}
                accent="emerald"
                icon={ICONS.currency}
              />
            )}
            {report.payment_type !== 'cash' && (
              <StatCard
                label={
                  report.payment_type === 'insurance' ? `جمع ${report.payment_type_label}` : 'بیمه'
                }
                {...rialMoneyStat(report.total_insurance)}
                tip={CARD_TIPS.insuranceAmount}
                accent="violet"
                icon={ICONS.currency}
              />
            )}
            {report.payment_type === 'all' && (
              <StatCard
                label={`جمع ${report.payment_type_label}`}
                {...rialMoneyStat(displayTotal)}
                tip={CARD_TIPS.totalAmount}
                accent="indigo"
                icon={ICONS.currency}
              />
            )}
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <h3 className="text-sm font-semibold text-slate-800">لیست نوبت‌ها</h3>
              <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-700">
                {report.items.length.toLocaleString('fa-IR')} مورد
              </span>
            </div>
            {report.items.length === 0 ? (
              <div className="px-6 py-14 text-center">
                <p className="text-sm font-medium text-slate-600">نوبت تاییدشده‌ای در این بازه نیست</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-right text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="px-3 py-2 font-medium">بیمار</th>
                      <th className="px-3 py-2 font-medium">کد ملی</th>
                      <th className="px-3 py-2 font-medium">موبایل</th>
                      <th className="px-3 py-2 font-medium">تاریخ نوبت</th>
                      <th className="px-3 py-2 font-medium">وضعیت</th>
                      <th className="px-3 py-2 font-medium">پذیرش HIS</th>
                      <th className="px-3 py-2 font-medium">تطبیق</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {report.items.map((item) => (
                      <tr key={item.appointment_id} className="hover:bg-slate-50/80">
                        <td className="px-3 py-2 font-medium text-slate-800">
                          {item.patient_name || '—'}
                        </td>
                        <td className="px-3 py-2 font-mono" dir="ltr">
                          {item.national_id || '—'}
                        </td>
                        <td className="px-3 py-2 font-mono" dir="ltr">
                          {item.phone || '—'}
                        </td>
                        <td className="px-3 py-2" dir="ltr">
                          {item.appointment_date}
                          {item.appointment_time ? ` ${item.appointment_time}` : ''}
                        </td>
                        <td className="px-3 py-2">
                          {item.matched ? (
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">
                              به ثمر رسیده
                            </span>
                          ) : (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-500">
                              بدون پذیرش
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2" dir="ltr">
                          {item.recept_date || '—'}
                        </td>
                        <td className="px-3 py-2">{matchMethodLabel(item.match_method)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {!report && !error && !loadingReport && (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-14 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-600">
            <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <p className="text-sm font-medium text-slate-700">گزارشی هنوز اجرا نشده</p>
          <p className="mx-auto mt-1 max-w-sm text-xs text-slate-500">
            بازه تاریخ نوبت آنلاین را انتخاب کنید و «اجرای گزارش» را بزنید
          </p>
        </div>
      )}

      <DetailModal
        open={detailOpen}
        loading={detailLoading}
        error={detailError}
        data={detailData}
        onClose={closeDetail}
      />
    </div>
  );
};

export default AdminHisOnlineConversion;
